import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, Save, Download, ArrowLeft, Upload } from 'lucide-react';
import { useLibrary } from '../contexts/LibraryContext';
import { useApi } from '../contexts/ApiContext';
import { useAIOperation } from '../hooks/useAIOperation';
import { ProviderRegistry } from '../services/providerRegistry';
import { ReplicateProvider } from '../services/providers/ReplicateProvider';
import ImagePicker from '../components/ImagePicker';
import ProviderToggle from '../components/ProviderToggle';
import ImageCompare from '../components/ImageCompare';
import JobStatus, { CostBadge } from '../components/JobStatus';
import type { MedaImage } from '../types/image';

type ReplaceMode = 'color' | 'gradient' | 'upload' | 'prompt';
type GradientDir = 'v' | 'h' | 'd';

type BgSpec =
  | { type: 'color'; color: string }
  | { type: 'gradient'; from: string; to: string; dir: GradientDir }
  | { type: 'image'; blob: Blob };

async function composite(fg: Blob, bg: BgSpec): Promise<Blob> {
  const fgBitmap = await createImageBitmap(fg);
  const W = fgBitmap.width;
  const H = fgBitmap.height;
  const canvas = new OffscreenCanvas(W, H);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    fgBitmap.close();
    throw new Error('Unable to get 2d context');
  }
  if (bg.type === 'color') {
    ctx.fillStyle = bg.color;
    ctx.fillRect(0, 0, W, H);
  } else if (bg.type === 'gradient') {
    const grad = bg.dir === 'v'
      ? ctx.createLinearGradient(0, 0, 0, H)
      : bg.dir === 'h'
      ? ctx.createLinearGradient(0, 0, W, 0)
      : ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, bg.from);
    grad.addColorStop(1, bg.to);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
  } else {
    const bgBitmap = await createImageBitmap(bg.blob);
    const scale = Math.max(W / bgBitmap.width, H / bgBitmap.height);
    const dw = bgBitmap.width * scale;
    const dh = bgBitmap.height * scale;
    ctx.drawImage(bgBitmap, (W - dw) / 2, (H - dh) / 2, dw, dh);
    bgBitmap.close();
  }
  ctx.drawImage(fgBitmap, 0, 0);
  fgBitmap.close();
  return canvas.convertToBlob({ type: 'image/png' });
}

async function alphaInverseMask(fg: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(fg);
  const W = bitmap.width;
  const H = bitmap.height;
  const canvas = new OffscreenCanvas(W, H);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('Unable to get 2d context');
  }
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  const data = ctx.getImageData(0, 0, W, H);
  for (let i = 0; i < data.data.length; i += 4) {
    const a = data.data[i + 3];
    const v = a > 0 ? 0 : 255;
    data.data[i] = v;
    data.data[i + 1] = v;
    data.data[i + 2] = v;
    data.data[i + 3] = 255;
  }
  ctx.putImageData(data, 0, 0);
  return canvas.convertToBlob({ type: 'image/png' });
}

// Small transparent-checkerboard styling via inline SVG data URI
const CHECKER_BG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20'><rect width='10' height='10' fill='%23333'/><rect x='10' y='10' width='10' height='10' fill='%23333'/><rect x='10' width='10' height='10' fill='%23222'/><rect y='10' width='10' height='10' fill='%23222'/></svg>\")";

export default function BackgroundPage() {
  const navigate = useNavigate();
  const { addImage } = useLibrary();
  const { tier, setTier, apiKeys } = useApi();

  // Two hook instances — one per stage
  const [s1, a1] = useAIOperation(); // remove_bg
  const [s2, a2] = useAIOperation(); // inpaint (prompt-mode bg fill)

  const [selectedImage, setSelectedImage] = useState<MedaImage | null>(null);
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);

  // Stage-2 controls
  const [mode, setMode] = useState<ReplaceMode>('color');
  const [color, setColor] = useState('#ffffff');
  const [gradFrom, setGradFrom] = useState('#111111');
  const [gradTo, setGradTo] = useState('#ffd166');
  const [gradDir, setGradDir] = useState<GradientDir>('v');
  const [bgImage, setBgImage] = useState<MedaImage | null>(null);
  const [prompt, setPrompt] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  // Composited final
  const [finalBlob, setFinalBlob] = useState<Blob | null>(null);
  const [finalUrl, setFinalUrl] = useState<string | null>(null);

  // Upload input ref for bg-image mode
  const bgUploadRef = useRef<HTMLInputElement>(null);

  // Cleanup object URLs on change/unmount
  useEffect(() => {
    return () => {
      if (originalUrl) URL.revokeObjectURL(originalUrl);
    };
  }, [originalUrl]);

  useEffect(() => {
    return () => {
      if (finalUrl) URL.revokeObjectURL(finalUrl);
    };
  }, [finalUrl]);

  const registry = useMemo(() => new ProviderRegistry({
    huggingfaceKey: apiKeys.huggingface,
    replicateKey: apiKeys.replicate,
    preferredTier: tier,
  }), [apiKeys.huggingface, apiKeys.replicate, tier]);

  const removeProvider = useMemo(() => registry.getBestProvider('remove_bg'), [registry]);
  const inpaintProvider = useMemo(() => registry.getBestProvider('inpaint'), [registry]);
  const removeCandidates = useMemo(() => registry.getProvidersForOp('remove_bg'), [registry]);
  const hasFree = removeCandidates.some(p => p.info.tier === 'free');
  const hasPaid = removeCandidates.some(p => p.info.tier === 'paid');

  const removeCost = useMemo(() => {
    if (!removeProvider) return 0;
    if (removeProvider.info.tier === 'free') return 0;
    if (removeProvider instanceof ReplicateProvider) return removeProvider.getEstimatedCost('remove_bg');
    return 0.01;
  }, [removeProvider]);

  const inpaintCost = useMemo(() => {
    if (!inpaintProvider) return 0;
    if (inpaintProvider.info.tier === 'free') return 0;
    if (inpaintProvider instanceof ReplicateProvider) return inpaintProvider.getEstimatedCost('inpaint');
    return 0.03;
  }, [inpaintProvider]);

  const clearFinal = useCallback(() => {
    if (finalUrl) URL.revokeObjectURL(finalUrl);
    setFinalBlob(null);
    setFinalUrl(null);
    setApplyError(null);
  }, [finalUrl]);

  const handleSelect = useCallback((image: MedaImage) => {
    setSelectedImage(image);
    if (originalUrl) URL.revokeObjectURL(originalUrl);
    setOriginalUrl(URL.createObjectURL(image.blob));
    a1.reset();
    a2.reset();
    clearFinal();
  }, [originalUrl, a1, a2, clearFinal]);

  const handleUploadFile = useCallback(async (file: File) => {
    const img = await addImage(file, file.name);
    handleSelect(img);
  }, [addImage, handleSelect]);

  const handleBgSelect = useCallback((image: MedaImage) => {
    setBgImage(image);
    clearFinal();
  }, [clearFinal]);

  const handleBgUploadFile = useCallback(async (file: File) => {
    const img = await addImage(file, file.name);
    setBgImage(img);
    clearFinal();
  }, [addImage, clearFinal]);

  const handleRemove = useCallback(async () => {
    if (!selectedImage || !removeProvider) return;
    clearFinal();
    a2.reset();
    await a1.run(removeProvider, 'remove_bg', selectedImage.blob);
  }, [selectedImage, removeProvider, a1, a2, clearFinal]);

  const handleApply = useCallback(async () => {
    if (!s1.resultBlob) return;
    setApplyError(null);
    setApplying(true);
    try {
      if (mode === 'color') {
        const out = await composite(s1.resultBlob, { type: 'color', color });
        if (finalUrl) URL.revokeObjectURL(finalUrl);
        setFinalBlob(out);
        setFinalUrl(URL.createObjectURL(out));
      } else if (mode === 'gradient') {
        const out = await composite(s1.resultBlob, { type: 'gradient', from: gradFrom, to: gradTo, dir: gradDir });
        if (finalUrl) URL.revokeObjectURL(finalUrl);
        setFinalBlob(out);
        setFinalUrl(URL.createObjectURL(out));
      } else if (mode === 'upload') {
        if (!bgImage) {
          setApplyError('Pick a background image first.');
          setApplying(false);
          return;
        }
        const out = await composite(s1.resultBlob, { type: 'image', blob: bgImage.blob });
        if (finalUrl) URL.revokeObjectURL(finalUrl);
        setFinalBlob(out);
        setFinalUrl(URL.createObjectURL(out));
      } else {
        // prompt mode → inpaint with inverse-alpha mask on the ORIGINAL image
        if (!selectedImage) {
          setApplyError('No source image.');
          setApplying(false);
          return;
        }
        if (!inpaintProvider) {
          setApplyError('No inpaint provider configured. Add a Replicate key in Settings.');
          setApplying(false);
          return;
        }
        if (!prompt.trim()) {
          setApplyError('Describe the background you want.');
          setApplying(false);
          return;
        }
        const mask = await alphaInverseMask(s1.resultBlob);
        const out = await a2.run(inpaintProvider, 'inpaint', selectedImage.blob, {
          mask,
          prompt: prompt.trim(),
        });
        if (out) {
          if (finalUrl) URL.revokeObjectURL(finalUrl);
          setFinalBlob(out);
          setFinalUrl(URL.createObjectURL(out));
        }
      }
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : String(err));
    } finally {
      setApplying(false);
    }
  }, [
    mode, color, gradFrom, gradTo, gradDir, bgImage, prompt,
    s1.resultBlob, selectedImage, inpaintProvider, a2, finalUrl,
  ]);

  const handleSave = useCallback(async () => {
    if (!finalBlob || !selectedImage) return;
    await addImage(
      finalBlob,
      `${selectedImage.name} (bg replaced)`,
      'generated',
      selectedImage.id,
      'background_replace',
    );
    navigate('/library');
  }, [finalBlob, selectedImage, addImage, navigate]);

  const handleSaveRemoved = useCallback(async () => {
    if (!s1.resultBlob || !selectedImage) return;
    await addImage(
      s1.resultBlob,
      `${selectedImage.name} (bg removed)`,
      'generated',
      selectedImage.id,
      'remove_bg',
    );
    navigate('/library');
  }, [s1.resultBlob, selectedImage, addImage, navigate]);

  const handleDownload = useCallback(() => {
    if (!finalUrl || !selectedImage) return;
    const a = document.createElement('a');
    a.href = finalUrl;
    a.download = `${selectedImage.name}_bg.png`;
    a.click();
  }, [finalUrl, selectedImage]);

  const noRemoveProvider = !removeProvider;
  const needsKey = noRemoveProvider && !apiKeys.huggingface && !apiKeys.replicate;
  const stage1Done = s1.status === 'succeeded' && !!s1.resultUrl;
  const stage2Busy = applying || s2.status === 'submitting' || s2.status === 'processing';

  return (
    <div className="flex flex-col h-full sage-animate-fade-in">
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 border-b"
        style={{ borderColor: 'var(--sage-white-5)', backgroundColor: 'var(--sage-sidebar)' }}
      >
        <button onClick={() => navigate('/')} className="p-1.5 rounded-lg" style={{ color: 'var(--sage-text-muted)' }}>
          <ArrowLeft size={18} />
        </button>
        <ImageIcon size={18} style={{ color: 'var(--sage-primary)' }} />
        <h1 className="text-sm font-medium flex-1">Background</h1>
        <ProviderToggle tier={tier} onToggle={setTier} freeAvailable={hasFree} paidAvailable={hasPaid} />
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left panel — controls */}
        <div
          className="w-72 p-4 border-r overflow-y-auto flex flex-col gap-5"
          style={{ borderColor: 'var(--sage-white-5)', backgroundColor: 'var(--sage-sidebar)' }}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--sage-text-muted)' }}>
              Select Image
            </p>
            <ImagePicker
              selectedId={selectedImage?.id ?? null}
              onSelect={handleSelect}
              onUpload={handleUploadFile}
            />
          </div>

          {selectedImage && (
            <>
              {/* STAGE 1 */}
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--sage-text-muted)' }}>
                  Step 1 — Remove Background
                </p>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs" style={{ color: 'var(--sage-text)' }}>
                    {removeProvider?.info.name || 'None configured'}
                  </span>
                  <CostBadge cost={removeCost} tier={removeProvider?.info.tier || 'free'} />
                </div>

                {needsKey ? (
                  <div
                    className="p-3 rounded-lg text-xs"
                    style={{ backgroundColor: 'color-mix(in srgb, var(--sage-accent) 10%, var(--sage-bg))', color: 'var(--sage-accent)' }}
                  >
                    Add an API key in Settings to enable background removal.
                    <button onClick={() => navigate('/settings')} className="underline ml-1">Open Settings</button>
                  </div>
                ) : (
                  <button
                    onClick={handleRemove}
                    disabled={s1.status === 'submitting' || s1.status === 'processing'}
                    className="w-full py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                    style={{ backgroundColor: 'var(--sage-primary)', color: '#1c1b1a' }}
                  >
                    {s1.status === 'processing' ? 'Removing...' : 'Remove Background'}
                  </button>
                )}

                <div className="mt-2">
                  <JobStatus
                    status={s1.status}
                    progress={s1.progress}
                    error={s1.error}
                    onRetry={handleRemove}
                  />
                </div>

                {stage1Done && (
                  <button
                    onClick={handleSaveRemoved}
                    className="w-full mt-2 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs"
                    style={{ backgroundColor: 'var(--sage-bg)', color: 'var(--sage-text-muted)', border: '1px solid var(--sage-white-10)' }}
                  >
                    <Save size={13} /> Save cutout only
                  </button>
                )}
              </div>

              {/* STAGE 2 */}
              {stage1Done && (
                <div className="flex flex-col gap-3 pt-2 border-t" style={{ borderColor: 'var(--sage-white-5)' }}>
                  <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: 'var(--sage-text-muted)' }}>
                    Step 2 — Replace Background
                  </p>

                  {/* Mode radio buttons */}
                  <div className="grid grid-cols-2 gap-1.5">
                    {(['color', 'gradient', 'upload', 'prompt'] as const).map(m => (
                      <button
                        key={m}
                        onClick={() => { setMode(m); clearFinal(); }}
                        className="py-1.5 rounded-lg text-xs font-medium capitalize transition-colors"
                        style={{
                          backgroundColor: mode === m ? 'var(--sage-primary)' : 'var(--sage-bg)',
                          color: mode === m ? '#1c1b1a' : 'var(--sage-text-muted)',
                          border: `1px solid ${mode === m ? 'var(--sage-primary)' : 'var(--sage-white-10)'}`,
                        }}
                      >
                        {m}
                      </button>
                    ))}
                  </div>

                  {/* Mode-specific controls */}
                  {mode === 'color' && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.15em] mb-1.5" style={{ color: 'var(--sage-text-muted)' }}>
                        Background Color
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={color}
                          onChange={e => { setColor(e.target.value); }}
                          className="w-10 h-10 rounded-lg cursor-pointer bg-transparent border-none"
                        />
                        <input
                          type="text"
                          value={color}
                          onChange={e => setColor(e.target.value)}
                          className="flex-1 px-2 py-1.5 rounded-lg text-xs"
                          style={{ backgroundColor: 'var(--sage-bg)', color: 'var(--sage-text)', border: '1px solid var(--sage-white-10)' }}
                        />
                      </div>
                    </div>
                  )}

                  {mode === 'gradient' && (
                    <div className="flex flex-col gap-2">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.15em] mb-1.5" style={{ color: 'var(--sage-text-muted)' }}>
                          From
                        </p>
                        <input
                          type="color"
                          value={gradFrom}
                          onChange={e => setGradFrom(e.target.value)}
                          className="w-full h-9 rounded-lg cursor-pointer bg-transparent border-none"
                        />
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.15em] mb-1.5" style={{ color: 'var(--sage-text-muted)' }}>
                          To
                        </p>
                        <input
                          type="color"
                          value={gradTo}
                          onChange={e => setGradTo(e.target.value)}
                          className="w-full h-9 rounded-lg cursor-pointer bg-transparent border-none"
                        />
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.15em] mb-1.5" style={{ color: 'var(--sage-text-muted)' }}>
                          Direction
                        </p>
                        <select
                          value={gradDir}
                          onChange={e => setGradDir(e.target.value as GradientDir)}
                          className="w-full px-2 py-1.5 rounded-lg text-xs"
                          style={{ backgroundColor: 'var(--sage-bg)', color: 'var(--sage-text)', border: '1px solid var(--sage-white-10)' }}
                        >
                          <option value="v">Top to Bottom</option>
                          <option value="h">Left to Right</option>
                          <option value="d">Diagonal</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {mode === 'upload' && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.15em] mb-1.5" style={{ color: 'var(--sage-text-muted)' }}>
                        Background Image
                      </p>
                      <ImagePicker
                        selectedId={bgImage?.id ?? null}
                        onSelect={handleBgSelect}
                        onUpload={handleBgUploadFile}
                      />
                      <input
                        ref={bgUploadRef}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) handleBgUploadFile(file);
                        }}
                      />
                    </div>
                  )}

                  {mode === 'prompt' && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.15em] mb-1.5" style={{ color: 'var(--sage-text-muted)' }}>
                        Describe Background
                      </p>
                      <textarea
                        value={prompt}
                        onChange={e => setPrompt(e.target.value)}
                        placeholder="e.g. sunset beach with palm trees, cinematic lighting"
                        rows={4}
                        className="w-full px-2 py-1.5 rounded-lg text-xs resize-none"
                        style={{ backgroundColor: 'var(--sage-bg)', color: 'var(--sage-text)', border: '1px solid var(--sage-white-10)' }}
                      />
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs" style={{ color: 'var(--sage-text)' }}>
                          {inpaintProvider?.info.name || 'No inpaint provider'}
                        </span>
                        {inpaintProvider && (
                          <CostBadge cost={inpaintCost} tier={inpaintProvider.info.tier} />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Apply button */}
                  <button
                    onClick={handleApply}
                    disabled={stage2Busy}
                    className="w-full py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                    style={{ backgroundColor: 'var(--sage-primary)', color: '#1c1b1a' }}
                  >
                    {stage2Busy ? 'Applying...' : 'Apply Background'}
                  </button>

                  {applyError && (
                    <div
                      className="px-3 py-2 rounded-lg text-xs"
                      style={{ backgroundColor: 'color-mix(in srgb, var(--sage-danger) 10%, var(--sage-surface))', color: 'var(--sage-danger)' }}
                    >
                      {applyError}
                    </div>
                  )}

                  {mode === 'prompt' && (
                    <JobStatus
                      status={s2.status}
                      progress={s2.progress}
                      error={s2.error}
                      onRetry={handleApply}
                    />
                  )}

                  {finalBlob && (
                    <div className="flex gap-2">
                      <button
                        onClick={handleSave}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium"
                        style={{ backgroundColor: 'var(--sage-primary)', color: '#1c1b1a' }}
                      >
                        <Save size={14} /> Save to Library
                      </button>
                      <button
                        onClick={handleDownload}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs"
                        style={{ backgroundColor: 'var(--sage-bg)', color: 'var(--sage-text-muted)', border: '1px solid var(--sage-white-10)' }}
                      >
                        <Download size={14} />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Main area — preview / comparison */}
        <div className="flex-1 flex items-center justify-center p-6 min-w-0">
          {!selectedImage ? (
            <div className="text-center">
              <ImageIcon size={48} style={{ color: 'var(--sage-white-10)' }} className="mx-auto mb-3" />
              <p className="text-sm" style={{ color: 'var(--sage-text-muted)' }}>
                Select an image from your library to start
              </p>
            </div>
          ) : finalUrl && originalUrl ? (
            <div className="w-full max-w-3xl">
              <ImageCompare
                beforeUrl={originalUrl}
                afterUrl={finalUrl}
                beforeLabel="Original"
                afterLabel="Replaced"
              />
            </div>
          ) : stage1Done && s1.resultUrl ? (
            <div className="max-w-lg">
              <div
                className="rounded-xl shadow-lg overflow-hidden"
                style={{
                  backgroundImage: CHECKER_BG,
                  backgroundRepeat: 'repeat',
                }}
              >
                <img
                  src={s1.resultUrl}
                  alt="Subject with transparent background"
                  className="max-h-[70vh] w-auto mx-auto block"
                />
              </div>
              <p className="text-xs text-center mt-3" style={{ color: 'var(--sage-text-muted)' }}>
                Background removed — pick a replacement mode on the left.
              </p>
            </div>
          ) : (
            <div className="max-w-lg">
              <img
                src={originalUrl || ''}
                alt={selectedImage.name}
                className="rounded-xl shadow-lg max-h-[70vh] object-contain"
              />
              <p className="text-xs text-center mt-3 flex items-center justify-center gap-1.5" style={{ color: 'var(--sage-text-muted)' }}>
                <Upload size={12} />
                {selectedImage.width}x{selectedImage.height}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
